"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
    FiAlertCircle,
    FiCheck,
    FiCheckSquare,
    FiRefreshCw,
    FiSearch,
    FiX,
} from "react-icons/fi"
import { FaClipboardCheck, FaFileExcel } from "react-icons/fa"
import toast from "react-hot-toast"
import axiosInstance from "../../libs/axios"
import { Pagination as PaginationUtils, PaginationType } from "../../utils/pagination.utils"
import { SearchableSelect } from "../../utils/select-with-search"
import { useAttendanceDensity, DensityToggle } from "./table-density"
import { LeaveQuotaHint } from "./leave-quota-hint"
import AttendanceLocationDetails, { formatDistanceKm, formatLatLng } from "./location_details"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { ExcelDownload, formatExcelDate, formatExcelDateTime } from "../../utils/excel.utils"
import {
    ATTENDANCE_LEAVE_TYPE_OPTIONS,
    DEFAULT_ATTENDANCE_LEAVE_TYPE,
    getAttendanceLeaveTypeLabel,
    getDisplayHRStatusLabel,
} from "../../libs/attendance"

/**
 * Attendance verifications — the second step of a manual mark.
 *
 * When a manager marks a day that had NO attendance row at all ("Not Marked"),
 * the day does not count until it is confirmed. This table lists those marks
 * made by the signed-in user and by everyone below them in the reporting
 * hierarchy (super admins see all), with the "decider" — the marker's
 * reporting person the mark was routed to.
 *
 * Viewing needs attendance.view_verifications. Approving / rejecting (one row
 * or many) needs attendance.approve_verifications; the API also refuses a row
 * the caller marked themselves or that is their own attendance, and says so
 * per row through `can_verify`.
 */

interface VerificationUser {
    id: number
    first_name?: string
    last_name?: string
    email?: string
    employee_code?: string | null
    designation?: { id?: number; name?: string } | null
    department?: { id?: number; name?: string } | null
}

interface VerificationRecord {
    id: number
    user_id: number
    attendance_date: string
    check_in_time?: string | null
    check_out_time?: string | null
    // Location captured with the punch (null on a manual mark). Distances are
    // metres from the user's reporting location.
    check_in_latitude?: number | null
    check_in_longitude?: number | null
    check_out_latitude?: number | null
    check_out_longitude?: number | null
    check_in_address?: string | null
    check_out_address?: string | null
    reporting_latitude?: number | null
    reporting_longitude?: number | null
    distance_from_reporting?: number | null
    check_out_distance_from_reporting?: number | null
    work_status: string
    hr_status: string
    leave_type?: string | null
    marking_type?: string | null
    verification_type?: string | null
    approver_remarks?: string | null
    verification_status: string
    verification_remarks?: string | null
    verified_at?: string | null
    created_at?: string
    // When the mark / change being verified was made.
    verification_requested_at?: string | null
    // Only on an attendance change request: what the day was approved as
    // before, and what the change asked for.
    change_request?: {
        previous: { work_status?: string | null; leave_type?: string | null; hr_status?: string | null }
        requested: { work_status?: string | null; leave_type?: string | null; remarks?: string | null }
    } | null
    user?: VerificationUser | null
    creator?: VerificationUser | null
    verifier?: VerificationUser | null
    decider?: VerificationUser | null
    can_verify?: boolean
}

interface PersonOption {
    id: number
    name: string
    employee_code?: string | null
}

interface FilterOptions {
    marked_by: PersonOption[]
    deciders: PersonOption[]
    employees: PersonOption[]
    work_statuses: string[]
}

type QueueStatus = "pending" | "approved" | "rejected" | "all"

interface Filters {
    verification_type: string
    work_status: string
    marked_by: string
    decider_id: string
    user_id: string
    date_from: string
    date_to: string
}

const EMPTY_FILTERS: Filters = {
    verification_type: "",
    work_status: "",
    marked_by: "",
    decider_id: "",
    user_id: "",
    date_from: "",
    date_to: "",
}

const WORK_STATUS_LABELS: Record<string, string> = {
    worked: "Worked",
    late: "Late",
    idle: "Idle",
    absent: "Absent",
    leave: "Leave Applied",
    weekly_off: "Weekly Off",
    holiday: "Holiday",
    work_from_home: "Work From Home",
    pending: "Pending",
}

const WORK_STATUS_CLASSES: Record<string, string> = {
    worked: "bg-green-100 text-green-700",
    late: "bg-amber-100 text-amber-700",
    idle: "bg-yellow-100 text-yellow-700",
    absent: "bg-red-100 text-red-700",
    leave: "bg-blue-100 text-blue-900",
    weekly_off: "bg-purple-100 text-purple-700",
    holiday: "bg-indigo-100 text-indigo-700",
    work_from_home: "bg-teal-100 text-teal-700",
    pending: "bg-gray-100 text-gray-700",
}

const VERIFICATION_CLASSES: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
}

// The work statuses the server treats as present on approval
// (applyVerificationDecision). Shown next to each option, display only.
const PRESENT_WORK_STATUSES = new Set(["worked", "late", "idle", "weekly_off", "holiday", "work_from_home"])
// Leave Applied is stored as HR "absent" but reads Leave Applied everywhere in
// Attendance, so its option carries no bracket.
const decisionOptionLabel = (status: string) =>
    status === "leave"
        ? WORK_STATUS_LABELS.leave
        : `${WORK_STATUS_LABELS[status]} (${PRESENT_WORK_STATUSES.has(status) ? "Present" : "Absent"})`

/** "Leave Applied (Casual)" for a Leave day with a type, otherwise the plain label. */
const workStatusText = (record: Pick<VerificationRecord, "work_status" | "leave_type">) => {
    const label = WORK_STATUS_LABELS[record.work_status] ?? record.work_status
    return record.work_status === "leave" && record.leave_type
        ? `${label} (${getAttendanceLeaveTypeLabel(record.leave_type)})`
        : label
}

const VERIFICATION_TYPE_LABELS: Record<string, string> = {
    manual_marking: "Manual Marking",
    attendance_change_request: "Attendance Change Request",
}

const isChangeRequest = (record?: Pick<VerificationRecord, "verification_type"> | null) =>
    record?.verification_type === "attendance_change_request"

/**
 * What the mark / change asked for. A rejected change request has had its
 * previous status restored on the row, so the request is read from
 * change_request instead.
 */
const requestedStatus = (record: VerificationRecord): Pick<VerificationRecord, "work_status" | "leave_type"> =>
    isChangeRequest(record) && record.change_request?.requested?.work_status
        ? {
            work_status: record.change_request.requested.work_status,
            leave_type: record.change_request.requested.leave_type,
        }
        : record

const MARKING_TYPE_LABELS: Record<string, string> = {
    auto: "Auto",
    manual: "Manual",
}

// The statuses a verifier may settle a day on. "pending" is not a decision, so
// it is not offered here.
const DECISION_WORK_STATUSES = [
    "worked",
    "late",
    "idle",
    "absent",
    "leave",
    "weekly_off",
    "holiday",
    "work_from_home",
]

const STATUS_TABS: Array<{ key: QueueStatus; label: string }> = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
]

const formatName = (person?: VerificationUser | null) => {
    if (!person) return "N/A"
    const name = `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim()
    return name || person.email || "N/A"
}

const formatDate = (value?: string | null) => {
    if (!value) return "N/A"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

const formatDateTime = (value?: string | null) => {
    if (!value) return "—"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "—"
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

const formatTime = (value?: string | null) => {
    if (!value) return "—"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "—"
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
}

const personOptions = (people: PersonOption[]) =>
    people.map((person) => ({
        value: String(person.id),
        label: person.employee_code ? `${person.name} (${person.employee_code})` : person.name,
    }))

/** One table row as it is exported — the same columns the table shows. */
const toExportRow = (record: VerificationRecord) => ({
    "Employee": formatName(record.user),
    "Employee Code": record.user?.employee_code || "",
    "Attendance Date": formatExcelDate(record.attendance_date),
    "Created Date": formatExcelDateTime(record.verification_requested_at || record.created_at),
    "Verification Type":
        VERIFICATION_TYPE_LABELS[record.verification_type || "manual_marking"] ?? record.verification_type ?? "",
    "Marking Type": record.marking_type ? MARKING_TYPE_LABELS[record.marking_type] ?? record.marking_type : "",
    "Previous Status":
        isChangeRequest(record) && record.change_request?.previous?.work_status
            ? workStatusText({
                work_status: record.change_request.previous.work_status,
                leave_type: record.change_request.previous.leave_type,
            })
            : "",
    "Marked As": workStatusText(requestedStatus(record)),
    "Check In": record.check_in_time ? formatTime(record.check_in_time) : "",
    "Check Out": record.check_out_time ? formatTime(record.check_out_time) : "",
    "Check In Lat/Long": formatLatLng(record.check_in_latitude, record.check_in_longitude) || "",
    "Check Out Lat/Long": formatLatLng(record.check_out_latitude, record.check_out_longitude) || "",
    "Check In Address": record.check_in_address || "",
    "Check Out Address": record.check_out_address || "",
    "Check In Distance (km)": formatDistanceKm(record.distance_from_reporting)?.replace(" km", "") || "",
    "Check Out Distance (km)": formatDistanceKm(record.check_out_distance_from_reporting)?.replace(" km", "") || "",
    "Marked By": record.creator ? formatName(record.creator) : "",
    "Decider": record.decider ? formatName(record.decider) : "",
    "Marker Remarks": record.change_request?.requested?.remarks || record.approver_remarks || "",
    "Status": record.verification_status,
    "HR Status": record.hr_status ? getDisplayHRStatusLabel(record.hr_status, record.work_status) : "",
    "Decided By": record.verifier ? formatName(record.verifier) : "",
    "Decided On": formatExcelDate(record.verified_at),
    "Remarks": record.verification_remarks || "",
})

/** Approve / reject one queued day, with an optional status correction. */
const DecisionModal: React.FC<{
    isOpen: boolean
    record: VerificationRecord | null
    decision: "approve" | "reject"
    verifyEndpoint?: string
    onClose: () => void
    onSuccess: () => void
}> = ({ isOpen, record, decision, verifyEndpoint = "/attendance/verify", onClose, onSuccess }) => {
    const [workStatus, setWorkStatus] = useState("worked")
    const [leaveType, setLeaveType] = useState(DEFAULT_ATTENDANCE_LEAVE_TYPE)
    const [remarks, setRemarks] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState("")
    const [quotaBlocked, setQuotaBlocked] = useState(false)

    useEffect(() => {
        if (isOpen && record) {
            setWorkStatus(
                DECISION_WORK_STATUSES.includes(record.work_status) ? record.work_status : "worked"
            )
            setLeaveType(record.leave_type || DEFAULT_ATTENDANCE_LEAVE_TYPE)
            setRemarks("")
            setError("")
        }
    }, [isOpen, record])

    if (!isOpen || !record) return null

    const isApproval = decision === "approve"

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setSubmitting(true)
        setError("")
        try {
            await axiosInstance.post(verifyEndpoint, {
                id: record.id,
                action: decision,
                // Only sent on approval: a rejection keeps whatever the marker
                // chose, it just never becomes an HR status.
                ...(isApproval ? { work_status: workStatus } : {}),
                // The verifier may change the leave type the marker picked.
                ...(isApproval && workStatus === "leave" ? { leave_type: leaveType } : {}),
                ...(remarks.trim() ? { verification_remarks: remarks.trim() } : {}),
            })
            onSuccess()
            onClose()
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to save your decision")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isApproval ? "Verify Attendance" : "Reject Verification"}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Employee</span>
                            <span className="font-medium text-gray-900">{formatName(record.user)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Attendance Date</span>
                            <span className="font-medium text-gray-900">{formatDate(record.attendance_date)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Marked by</span>
                            <span className="font-medium text-gray-900">{formatName(record.creator)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Decider</span>
                            <span className="font-medium text-gray-900">{formatName(record.decider)}</span>
                        </div>
                        {isChangeRequest(record) && record.change_request?.previous?.work_status && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Approved as (before)</span>
                                <span className="font-medium text-gray-900">
                                    {workStatusText({
                                        work_status: record.change_request.previous.work_status,
                                        leave_type: record.change_request.previous.leave_type,
                                    })}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span className="text-gray-500">{isChangeRequest(record) ? "Changed to" : "Marked as"}</span>
                            <span className="font-medium text-gray-900">
                                {workStatusText(record)}
                            </span>
                        </div>
                    </div>

                    <AttendanceLocationDetails record={record} />

                    {isApproval ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Work Status
                            </label>
                            <select
                                value={workStatus}
                                onChange={(e) => setWorkStatus(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {DECISION_WORK_STATUSES.map((status) => (
                                    <option key={status} value={status}>
                                        {decisionOptionLabel(status)}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                Defaults to what the marker chose. The HR status in brackets is what the day becomes.
                            </p>
                            {workStatus === "leave" && (
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Leave Type
                                    </label>
                                    <select
                                        value={leaveType}
                                        onChange={(e) => setLeaveType(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {ATTENDANCE_LEAVE_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Defaults to the type the marker chose (Casual if none).
                                    </p>
                                    <LeaveQuotaHint
                                        userId={record.user_id}
                                        date={record.attendance_date}
                                        leaveType={leaveType}
                                        onBlockedChange={setQuotaBlocked}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-start gap-2 rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                            <FiAlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                                {isChangeRequest(record)
                                    ? "The change is refused and the day goes back to the status it was approved as before."
                                    : "The day stays on record but never counts as present. The person who marked it can mark it again, which sends it back for verification."}
                            </span>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Remarks {isApproval ? "(optional)" : ""}
                        </label>
                        <textarea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={3}
                            maxLength={1000}
                            placeholder={isApproval ? "Anything worth recording" : "Why is this being rejected?"}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {error && (
                        <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 text-sm font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || (isApproval && workStatus === "leave" && quotaBlocked)}
                            className={`flex-1 px-4 py-2 rounded text-white text-sm font-medium disabled:opacity-60 ${isApproval ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                                }`}
                        >
                            {submitting ? "Saving..." : isApproval ? "Verify" : "Reject"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

const AttendanceVerifications: React.FC<{ projectScoped?: boolean }> = ({ projectScoped = false }) => {
    const { user, hasPermission } = useAuth()
    const { confirm, prompt } = useConfirm()
    const density = useAttendanceDensity()
    // Human Resource is an audit view only. The Attendance/Leaves queue keeps
    // its existing approve/reject controls and permissions.
    const canApprove = !projectScoped && hasPermission("attendance", "approve_verifications")
    const listEndpoint = projectScoped ? "/attendance/hr-verifications" : "/attendance/verifications"
    const filterOptionsEndpoint = projectScoped
        ? "/attendance/hr-verifications/filter-options"
        : "/attendance/verifications/filter-options"
    const verifyEndpoint = "/attendance/verify"
    const bulkVerifyEndpoint = "/attendance/verify/bulk"

    const [records, setRecords] = useState<VerificationRecord[]>([])
    const [counts, setCounts] = useState<Record<string, number>>({ pending: 0, approved: 0, rejected: 0 })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [queueStatus, setQueueStatus] = useState<QueueStatus>("pending")
    const [searchInput, setSearchInput] = useState("")
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        marked_by: [],
        deciders: [],
        employees: [],
        work_statuses: [],
    })
    const [pagination, setPagination] = useState<PaginationType>({
        page: 1,
        limit: 25,
        total_count: 0,
        total_pages: 0,
        offset: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    })
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [bulkBusy, setBulkBusy] = useState(false)
    const [decisionRecord, setDecisionRecord] = useState<VerificationRecord | null>(null)
    const [decision, setDecision] = useState<"approve" | "reject">("approve")
    const [exportRows, setExportRows] = useState<Array<Record<string, unknown>>>([])
    const [exporting, setExporting] = useState(false)
    const [showExcelModal, setShowExcelModal] = useState(false)

    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput.trim()), 350)
        return () => clearTimeout(handle)
    }, [searchInput])

    useEffect(() => {
        axiosInstance
            .get(filterOptionsEndpoint)
            .then((response) => {
                const data = response.data?.data
                if (data) {
                    setFilterOptions({
                        marked_by: data.marked_by || [],
                        deciders: data.deciders || [],
                        employees: data.employees || [],
                        work_statuses: data.work_statuses || [],
                    })
                }
            })
            .catch(() => {
                // The table still works without dropdown options.
            })
    }, [filterOptionsEndpoint])

    const fetchRecords = useCallback(
        async (page = pagination.page, limit = pagination.limit) => {
            setLoading(true)
            setError("")
            try {
                const params: Record<string, string | number> = { page, limit, status: queueStatus }
                if (search) params.search = search
                ;(Object.keys(filters) as Array<keyof Filters>).forEach((key) => {
                    if (filters[key]) params[key] = filters[key]
                })
                const response = await axiosInstance.get(listEndpoint, { params })
                setRecords(response.data?.data?.attendances || [])
                if (response.data?.data?.counts) setCounts(response.data.data.counts)
                if (response.data?.data?.pagination) {
                    setPagination(response.data.data.pagination)
                }
            } catch (err: any) {
                setError(err.response?.data?.message || "Failed to load verifications")
                setRecords([])
            } finally {
                setLoading(false)
                setSelectedIds([])
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [queueStatus, search, filters, listEndpoint]
    )

    useEffect(() => {
        fetchRecords(1, pagination.limit)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queueStatus, search, filters])

    const isSuperAdmin = user?.role?.level === 1
    const cellClass = `${density.bodyCell} whitespace-nowrap`
    const selectableRecords = useMemo(
        () => (canApprove ? records.filter((record) => record.can_verify) : []),
        [records, canApprove]
    )
    const showSelection = canApprove && (queueStatus === "pending" || queueStatus === "all")
    const allSelected =
        selectableRecords.length > 0 && selectableRecords.every((record) => selectedIds.includes(record.id))
    const activeFilterCount =
        Object.values(filters).filter(Boolean).length + (search ? 1 : 0)

    const setFilter = (key: keyof Filters, value: string) =>
        setFilters((prev) => ({ ...prev, [key]: value }))

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS)
        setSearchInput("")
    }

    const toggleRow = (id: number) =>
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))

    const toggleAll = () =>
        setSelectedIds(allSelected ? [] : selectableRecords.map((record) => record.id))

    const runBulk = async (action: "approve" | "reject") => {
        if (selectedIds.length === 0) return
        const count = selectedIds.length
        let remarks: string | null | undefined
        if (action === "reject") {
            remarks = await prompt({
                title: `Reject ${count} verification${count === 1 ? "" : "s"}?`,
                message: "The days stay on record but never count as present. The marker can mark them again.",
                label: "Reason",
                placeholder: "Why are these being rejected?",
                required: true,
                multiline: true,
                maxLength: 1000,
                confirmText: "Reject",
                cancelText: "Go back",
                variant: "danger",
            })
            if (remarks === null) return
        } else {
            const ok = await confirm({
                title: `Verify ${count} attendance record${count === 1 ? "" : "s"}?`,
                message: "Each day is approved with the work status it was marked as, and becomes Present or Absent for HR accordingly.",
                confirmText: "Verify",
                cancelText: "Go back",
                variant: "info",
            })
            if (!ok) return
        }

        setBulkBusy(true)
        try {
            const response = await axiosInstance.post(bulkVerifyEndpoint, {
                ids: selectedIds,
                action,
                ...(remarks && remarks.trim() ? { verification_remarks: remarks.trim() } : {}),
            })
            const { updated_count = 0, skipped = [] } = response.data?.data || {}
            if (updated_count > 0) {
                toast.success(
                    `${updated_count} record${updated_count === 1 ? "" : "s"} ${action === "approve" ? "verified" : "rejected"}`
                )
            }
            if (Array.isArray(skipped) && skipped.length > 0) {
                toast.error(
                    `${skipped.length} record${skipped.length === 1 ? " was" : "s were"} skipped: ${skipped[0].reason}`
                )
            }
            await fetchRecords(pagination.page, pagination.limit)
        } catch (err: any) {
            toast.error(err.response?.data?.message || "Bulk action failed")
        } finally {
            setBulkBusy(false)
        }
    }

    // Every row the current tab and filters match, not just the page on
    // screen — fetched from the same endpoint, so the same scope applies.
    const handleExport = async () => {
        setExporting(true)
        try {
            const params: Record<string, string | number> = { limit: 200, status: queueStatus }
            if (search) params.search = search
            ;(Object.keys(filters) as Array<keyof Filters>).forEach((key) => {
                if (filters[key]) params[key] = filters[key]
            })
            const all: VerificationRecord[] = []
            let page = 1
            let totalPages = 1
            do {
                const response = await axiosInstance.get(listEndpoint, { params: { ...params, page } })
                all.push(...(response.data?.data?.attendances || []))
                totalPages = response.data?.data?.pagination?.total_pages || 1
                page += 1
            } while (page <= totalPages)
            if (all.length === 0) {
                toast.error("No verifications to export")
                return
            }
            setExportRows(all.map(toExportRow))
            setShowExcelModal(true)
        } catch (err: any) {
            toast.error(err.response?.data?.message || "Failed to export verifications")
        } finally {
            setExporting(false)
        }
    }

    const emptyMessage = useMemo(() => {
        if (activeFilterCount > 0) return "No verifications match these filters."
        if (queueStatus === "pending") return "Nothing is waiting for verification."
        if (queueStatus === "all") return "No manual marks or attendance changes have gone through verification yet."
        return queueStatus === "approved" ? "No verified attendance yet." : "No rejected attendance yet."
    }, [queueStatus, activeFilterCount])

    const columnCount = (projectScoped ? 19 : 20) + (showSelection ? 1 : 0)
    const selectOptions = (people: PersonOption[]) => personOptions(people)
    const selectedOption = (people: PersonOption[], id: string) =>
        selectOptions(people).find((option) => option.value === id) || null

    return (
        <>
            <div className="p-4 space-y-4">
                <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
                    {/* Header */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded bg-blue-50 text-blue-800 flex items-center justify-center">
                                    <FaClipboardCheck className="w-5 h-5" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900">Attendance Verifications</h1>
                                    <p className="text-sm text-gray-500">
                                        {projectScoped
                                            ? "Manual attendance marks for employees assigned to your projects. Reporting-manager hierarchy does not restrict this view."
                                            : "Days marked by you and your team that had no attendance at all, and changes made to days that were already approved. They count only once verified."}
                                        {!projectScoped && isSuperAdmin && " As a super admin you see every verification."}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <DensityToggle density={density} />
                                {projectScoped && (
                                    <button
                                        onClick={handleExport}
                                        disabled={exporting}
                                        className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 bg-white disabled:opacity-60"
                                    >
                                        <FaFileExcel className="w-4 h-4 text-green-700" />
                                        {exporting ? "Preparing..." : "Excel"}
                                    </button>
                                )}
                                <button
                                    onClick={() => fetchRecords(pagination.page, pagination.limit)}
                                    className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 bg-white"
                                >
                                    <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Status tabs */}
                    <div className="px-4 sm:px-6 pt-3 border-b border-gray-200 flex flex-wrap gap-1">
                        {STATUS_TABS.map((tab) => {
                            const count =
                                tab.key === "all"
                                    ? (counts.pending || 0) + (counts.approved || 0) + (counts.rejected || 0)
                                    : counts[tab.key] || 0
                            const active = queueStatus === tab.key
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setQueueStatus(tab.key)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${active
                                        ? "border-blue-800 text-blue-800"
                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                        }`}
                                >
                                    {tab.label}
                                    <span
                                        className={`min-w-5 px-1.5 py-0.5 rounded-full text-xs ${active ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"
                                            }`}
                                    >
                                        {count}
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    {/* Filters */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 items-end">
                            <div className="xl:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        placeholder="Name, code or email"
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Verification Type</label>
                                <select
                                    value={filters.verification_type}
                                    onChange={(e) => setFilter("verification_type", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All</option>
                                    {Object.entries(VERIFICATION_TYPE_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Marked As</label>
                                <select
                                    value={filters.work_status}
                                    onChange={(e) => setFilter("work_status", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All</option>
                                    {(filterOptions.work_statuses.length ? filterOptions.work_statuses : DECISION_WORK_STATUSES).map(
                                        (status) => (
                                            <option key={status} value={status}>
                                                {WORK_STATUS_LABELS[status] ?? status}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Marked By</label>
                                <SearchableSelect
                                    value={selectedOption(filterOptions.marked_by, filters.marked_by)}
                                    onChange={(option: any) => setFilter("marked_by", option?.value ? String(option.value) : "")}
                                    options={selectOptions(filterOptions.marked_by)}
                                    placeholder="Anyone"
                                    emptyMessage="No markers"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Decider</label>
                                <SearchableSelect
                                    value={selectedOption(filterOptions.deciders, filters.decider_id)}
                                    onChange={(option: any) => setFilter("decider_id", option?.value ? String(option.value) : "")}
                                    options={selectOptions(filterOptions.deciders)}
                                    placeholder="Anyone"
                                    emptyMessage="No deciders"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                                <input
                                    type="date"
                                    value={filters.date_from}
                                    max={filters.date_to || undefined}
                                    onChange={(e) => setFilter("date_from", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                                <input
                                    type="date"
                                    value={filters.date_to}
                                    min={filters.date_from || undefined}
                                    onChange={(e) => setFilter("date_to", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        {activeFilterCount > 0 && (
                            <div className="mt-3 flex items-center gap-2 text-sm">
                                <span className="text-gray-600">
                                    {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
                                </span>
                                <button onClick={clearFilters} className="text-blue-700 hover:text-blue-900 underline">
                                    Clear all
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Bulk bar */}
                    {showSelection && selectedIds.length > 0 && (
                        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3 text-sm">
                                <FiCheckSquare className="w-5 h-5 text-blue-800" />
                                <span className="font-semibold text-blue-900">
                                    {selectedIds.length} selected
                                </span>
                                <button
                                    onClick={() => setSelectedIds([])}
                                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                                >
                                    Clear selection
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => runBulk("approve")}
                                    disabled={bulkBusy}
                                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                                >
                                    <FiCheck className="w-4 h-4" />
                                    {bulkBusy ? "Working…" : "Verify Selected"}
                                </button>
                                <button
                                    onClick={() => runBulk("reject")}
                                    disabled={bulkBusy}
                                    className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded hover:bg-red-50 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                                >
                                    <FiX className="w-4 h-4" />
                                    Reject Selected
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mx-4 sm:mx-6 mt-4 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {showSelection && (
                                        <th className={`${density.headCell} text-left w-10`}>
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                checked={allSelected}
                                                disabled={selectableRecords.length === 0}
                                                onChange={toggleAll}
                                                title="Select every row you can verify on this page"
                                            />
                                        </th>
                                    )}
                                    {!projectScoped && <th className={`${density.headCell} text-left`}>Actions</th>}
                                    <th className={`${density.headCell} text-left`}>Employee</th>
                                    <th className={`${density.headCell} text-left`}>Attendance Date</th>
                                    <th className={`${density.headCell} text-left`}>Created Date</th>
                                    <th className={`${density.headCell} text-left`}>Verification Type</th>
                                    <th className={`${density.headCell} text-left`}>Marking Type</th>
                                    <th className={`${density.headCell} text-left`}>Previous Status</th>
                                    <th className={`${density.headCell} text-left`}>Marked As</th>
                                    <th className={`${density.headCell} text-left`}>Check In</th>
                                    <th className={`${density.headCell} text-left`}>Check Out</th>
                                    <th className={`${density.headCell} text-left`}>Check-in Distance</th>
                                    <th className={`${density.headCell} text-left`}>Check-out Distance</th>
                                    <th className={`${density.headCell} text-left`}>Marked By</th>
                                    <th className={`${density.headCell} text-left`}>Decider</th>
                                    <th className={`${density.headCell} text-left`}>Marker Remarks</th>
                                    <th className={`${density.headCell} text-left`}>Status</th>
                                    <th className={`${density.headCell} text-left`}>HR Status</th>
                                    <th className={`${density.headCell} text-left`}>Decided By</th>
                                    <th className={`${density.headCell} text-left`}>Decided On</th>
                                    <th className={`${density.headCell} text-left`}>Remarks</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={columnCount} className={`${density.placeholderCell} text-center`}>
                                            Loading...
                                        </td>
                                    </tr>
                                ) : records.length === 0 ? (
                                    <tr>
                                        <td colSpan={columnCount} className={`${density.placeholderCell} text-center text-gray-500`}>
                                            {emptyMessage}
                                        </td>
                                    </tr>
                                ) : (
                                    records.map((record) => {
                                        const actionable = canApprove && Boolean(record.can_verify)
                                        const isPending = record.verification_status === "pending"
                                        return (
                                            <tr
                                                key={record.id}
                                                className={`hover:bg-gray-50 ${selectedIds.includes(record.id) ? "bg-blue-50/60" : ""}`}
                                            >
                                                {showSelection && (
                                                    <td className={cellClass}>
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                                                            checked={selectedIds.includes(record.id)}
                                                            disabled={!actionable}
                                                            onChange={() => toggleRow(record.id)}
                                                            title={actionable ? "Select" : "You cannot verify this row"}
                                                        />
                                                    </td>
                                                )}
                                                {!projectScoped && (
                                                    <td className={cellClass}>
                                                        {actionable ? (
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => {
                                                                    setDecision("approve")
                                                                    setDecisionRecord(record)
                                                                }}
                                                                className="p-2 text-green-700 hover:bg-green-50 rounded"
                                                                title="Verify"
                                                            >
                                                                <FiCheck className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setDecision("reject")
                                                                    setDecisionRecord(record)
                                                                }}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                                title="Reject"
                                                            >
                                                                <FiX className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span
                                                            className="text-xs text-gray-400"
                                                            title={
                                                                isPending
                                                                    ? "Waiting on someone who can verify it"
                                                                    : "Already decided"
                                                            }
                                                        >
                                                            {isPending ? "Awaiting" : "—"}
                                                        </span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className={cellClass}>
                                                    <div className="font-medium text-gray-900">{formatName(record.user)}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {record.user?.employee_code || record.user?.email || ""}
                                                    </div>
                                                </td>
                                                <td className={cellClass}>{formatDate(record.attendance_date)}</td>
                                                <td className={cellClass}>
                                                    {formatDateTime(record.verification_requested_at || record.created_at)}
                                                </td>
                                                <td className={cellClass}>
                                                    {VERIFICATION_TYPE_LABELS[record.verification_type || "manual_marking"] ?? record.verification_type}
                                                </td>
                                                <td className={cellClass}>
                                                    {record.marking_type ? MARKING_TYPE_LABELS[record.marking_type] ?? record.marking_type : "—"}
                                                </td>
                                                <td className={cellClass}>
                                                    {isChangeRequest(record) && record.change_request?.previous?.work_status
                                                        ? workStatusText({
                                                            work_status: record.change_request.previous.work_status,
                                                            leave_type: record.change_request.previous.leave_type,
                                                        })
                                                        : "—"}
                                                </td>
                                                <td className={cellClass}>
                                                    <span
                                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${WORK_STATUS_CLASSES[requestedStatus(record).work_status] ?? WORK_STATUS_CLASSES.pending}`}
                                                    >
                                                        {workStatusText(requestedStatus(record))}
                                                    </span>
                                                </td>
                                                <td className={cellClass}>{formatTime(record.check_in_time)}</td>
                                                <td className={cellClass}>{formatTime(record.check_out_time)}</td>
                                                <td className={cellClass}>
                                                    {formatDistanceKm(record.distance_from_reporting) ?? <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className={cellClass}>
                                                    {formatDistanceKm(record.check_out_distance_from_reporting) ?? <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className={cellClass}>{formatName(record.creator)}</td>
                                                <td className={cellClass}>
                                                    {record.decider ? formatName(record.decider) : <span className="text-gray-400">No reporting person</span>}
                                                </td>
                                                <td
                                                    className={`${density.bodyCell} max-w-xs truncate`}
                                                    title={record.change_request?.requested?.remarks || record.approver_remarks || ""}
                                                >
                                                    {record.change_request?.requested?.remarks || record.approver_remarks || "—"}
                                                </td>
                                                <td className={cellClass}>
                                                    <span
                                                        className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${VERIFICATION_CLASSES[record.verification_status] ?? "bg-gray-100 text-gray-700"}`}
                                                    >
                                                        {record.verification_status}
                                                    </span>
                                                </td>
                                                <td className={cellClass}>
                                                    {record.hr_status ? getDisplayHRStatusLabel(record.hr_status, record.work_status) : "—"}
                                                </td>
                                                <td className={cellClass}>{record.verifier ? formatName(record.verifier) : "—"}</td>
                                                <td className={cellClass}>{record.verified_at ? formatDate(record.verified_at) : "—"}</td>
                                                <td className={`${density.bodyCell} max-w-xs truncate`} title={record.verification_remarks || ""}>
                                                    {record.verification_remarks || "—"}
                                                </td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination.total_count > 0 && (
                        <div className="border-t border-gray-200">
                            <PaginationUtils
                                pagination={pagination}
                                onPageChange={(page) => fetchRecords(page, pagination.limit)}
                                onLimitChange={(limit) => fetchRecords(1, limit)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <DecisionModal
                isOpen={Boolean(decisionRecord)}
                record={decisionRecord}
                decision={decision}
                verifyEndpoint={verifyEndpoint}
                onClose={() => setDecisionRecord(null)}
                onSuccess={() => fetchRecords(pagination.page, pagination.limit)}
            />

            <ExcelDownload
                isOpen={showExcelModal}
                onClose={() => setShowExcelModal(false)}
                data={exportRows}
                filename="attendance_verifications"
                title="Export Attendance Verifications"
                description="Download the attendance verifications matching the current tab and filters"
            />
        </>
    )
}

export default AttendanceVerifications
